const PERSIST_KEY = 'vuex'
const CHANNEL_NAME = 'mtg-deck-builder-sync'

const clone = (obj) =>
	typeof structuredClone === 'function'
		? structuredClone(obj)
		: JSON.parse(JSON.stringify(obj))

const deckMutations = new Set([
	'decks',
	'addNewCardToGroup',
	'removeCardFromGroup',
	'mutateActiveDeck',
	'mutateCard',
	'resetListSorting',
	'mutateViewedCard',
])

const otherPersisted = new Set([
	'loadDefaultDecks',
	'permanentlyHiddenNotifications',
	'numUserVisitsToDonatePage',
	'updateJustInstalled',
	'waitingToApplyUpdate',
])

// Helper produces strictly increasing timestamp
const makeTimestampFactory = () => {
	let last = 0

	return () => {
		const now = Date.now()

		if (now <= last) last += 1
		else last = now

		return last
	}
}

let broadcastClosed = false

export default function dataPersistAndSync(store) {
	const nextTS = makeTimestampFactory()
	const tabId = Math.random().toString(36).slice(2)
	let dirty = false
	let timer = null
	let closing = false

	function buildSnapshot(state) {
		return {
			timeAppLastModified: state.timeAppLastModified,
			tabId,
			data: {
				decks: clone(state.decks),
				loadDefaultDecks: state.loadDefaultDecks,
				permanentlyHiddenNotifications: clone(
					state.permanentlyHiddenNotifications,
				),
				numUserVisitsToDonatePage: state.numUserVisitsToDonatePage,
				updateJustInstalled: state.updateJustInstalled,
				waitingToApplyUpdate: state.waitingToApplyUpdate,
			},
		}
	}

	/* Ensure that persisted state data (a "snapshot") is in a consistent format, regardless of whether it comes from a legacy or current version of the app. This normalization allows the rest of the app to work with a predictable data structure, supporting backward compatibility with older saved data. */
	function normalizeSnapshot(raw) {
		if (!raw) return null

		if (raw.data && raw.timeAppLastModified) return raw // New shape

		// Legacy shape: wrap it
		return {
			timeAppLastModified: raw.timeAppLastModified || 0,
			tabId: raw.tabId || 'legacy',
			data: {
				decks: raw.decks || [],
				activeDeckName: null,
				loadDefaultDecks:
					typeof raw.loadDefaultDecks === 'boolean'
						? raw.loadDefaultDecks
						: true,
				permanentlyHiddenNotifications:
					raw.permanentlyHiddenNotifications || [],
				updateJustInstalled: raw.updateJustInstalled,
				waitingToApplyUpdate: raw.waitingToApplyUpdate || false,
			},
		}
	}

	function applySnapshot(snap) {
		store.commit('___APPLY_SNAPSHOT', snap)
	}

	function readLocal() {
		try {
			const raw = localStorage.getItem(PERSIST_KEY)

			if (!raw) return null

			return normalizeSnapshot(JSON.parse(raw))
		} catch {
			return null
		}
	}

	function maybeFlush() {
		if (!dirty) return

		dirty = false
		const snapshot = buildSnapshot(store.state)

		localStorage.setItem(PERSIST_KEY, JSON.stringify(snapshot))

		if (!broadcastClosed) {
			bc.postMessage({ type: 'SNAPSHOT', snapshot })
		}
	}

	function scheduleFlush() {
		if (timer) return

		timer = setTimeout(() => {
			timer = null
			maybeFlush()
		}, 40) // Small debounce
	}

	// Initial load
	const initial = readLocal()

	if (initial) {
		applySnapshot(initial)
	} else {
		// First tab sets a baseline timestamp
		store.commit('___PERSIST_TOUCH', nextTS())
		maybeFlush()
	}

	// BroadcastChannel
	const bc = new BroadcastChannel(CHANNEL_NAME)

	bc.onmessage = (e) => {
		const msg = e.data

		if (!msg) return

		if (msg.type === 'REQUEST_LATEST') {
			if (store.state.timeAppLastModified > msg.have) {
				bc.postMessage({
					type: 'SNAPSHOT',
					snapshot: buildSnapshot(store.state),
				})
			}
		} else if (msg.type === 'SNAPSHOT' && msg.snapshot) {
			const snap = normalizeSnapshot(msg.snapshot)

			if (
				!snap.timeAppLastModified ||
				snap.timeAppLastModified < store.state.timeAppLastModified
			)
				return

			if (
				snap.timeAppLastModified === store.state.timeAppLastModified &&
				snap.tabId === tabId
			) {
				return
			}

			applySnapshot(snap)
		}
	}

	// Storage event fallback (other tabs write localStorage)
	window.addEventListener('storage', (e) => {
		if (e.key !== PERSIST_KEY || !e.newValue) return

		const snap = normalizeSnapshot(JSON.parse(e.newValue))

		if (snap.timeAppLastModified > store.state.timeAppLastModified) {
			applySnapshot(snap)
		}
	})

	// After mount, request latest (ensures a just‑opened stale tab updates)
	setTimeout(() => {
		bc.postMessage({
			type: 'REQUEST_LATEST',
			have: store.state.timeAppLastModified,
		})
	}, 60)

	// Subscribe to mutations
	store.subscribe((mutation) => {
		if (mutation.type === '___APPLY_SNAPSHOT') return

		let touched = false

		if (deckMutations.has(mutation.type) || otherPersisted.has(mutation.type)) {
			store.commit('___PERSIST_TOUCH', nextTS())
			touched = true
		}

		if (touched) {
			dirty = true
			scheduleFlush()
		}
	})

	function finalFlush() {
		if (closing) return

		closing = true

		if (timer) {
			clearTimeout(timer)
			timer = null
		}
		if (dirty) {
			maybeFlush()
		}

		broadcastClosed = true

		bc.close()
	}

	window.addEventListener('pagehide', finalFlush, { passive: true })

	window.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'hidden') finalFlush()
	})

	window.addEventListener('beforeunload', finalFlush)
}
